// frontend/src/components/auditor/AuditorTimeBilling.js
// Time entries, expense entries, and WIP summary for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import { formatNumber } from '../../utils/currency';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'time', label: 'Time Entries' },
  { id: 'expenses', label: 'Expenses' },
];

const fmt = (v) => (v == null ? '—' : formatNumber(v));
const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : 'â';
const fmtHrs = (v) => v != null ? Number(v).toFixed(2) : 'â';

export default function AuditorTimeBilling({ authAxios, dateRange }) {
  const [subTab, setSubTab] = useState('time');
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [te, ex] = await Promise.all([
      fetchAllPages(authAxios, 'time-entries/'),
      fetchAllPages(authAxios, 'expense-entries/'),
    ]);
    setTimeEntries(te);
    setExpenses(ex);
    if (te.length === 0 && ex.length === 0) {
      setFetchError('No time/billing data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Aggregate stats
  // [Auditor figures reconciliation] TimeEntrySerializer never exposes
  // `hours`/`billable`/`is_billed`/`total_value`/`amount` under those exact
  // names â `hours` is a manual-override field that's usually null (the
  // real duration is the serializer's computed `duration_hours`, which
  // already falls back to start_time/end_time when `hours` is unset);
  // `is_billable` (not `billable`) is the real boolean; there is no
  // `is_billed` boolean at all, only a `billing_status` enum
  // (UNBILLED/BILLED/WRITTEN_OFF/NON_BILLABLE); and there is no
  // precomputed value field â `duration_hours * effective_rate` is the
  // same formula TimeEntry.effective_rate itself documents
  // ("rate_override > billing_rate"). Every stat below previously read
  // 0.00 (or, for unbilledTime, the FULL total regardless of billing
  // status, since `!undefined` is always true) for every real entry.
  const entryHours = (e) => Number(e.duration_hours ?? e.hours ?? 0);
  const entryValue = (e) => entryHours(e) * Number(e.effective_rate ?? e.billing_rate ?? 0);
  const totalHours = timeEntries.reduce((s, e) => s + entryHours(e), 0);
  const billableHours = timeEntries.filter(e => e.is_billable).reduce((s, e) => s + entryHours(e), 0);
  const totalTimeValue = timeEntries.reduce((s, e) => s + entryValue(e), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const unbilledTime = timeEntries.filter(e => e.billing_status === 'UNBILLED').reduce((s, e) => s + entryValue(e), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Time &amp; Billing</h2>
        <p className="text-sm text-gray-500 mt-1">Time recordings, expenses, and work-in-progress.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Hours', value: fmtHrs(totalHours), color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Billable Hours', value: fmtHrs(billableHours), color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Time Value', value: `P ${fmt(totalTimeValue)}`, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Unbilled WIP', value: `P ${fmt(unbilledTime)}`, color: 'text-amber-700 bg-amber-50 border-amber-200' },
          { label: 'Total Expenses', value: `P ${fmt(totalExpenses)}`, color: 'text-red-700 bg-red-50 border-red-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'time' && (
        <AuditorTable
          title={`Time Entries (${timeEntries.length})`}
          data={timeEntries}
          columns={[
            { key: 'start_time', label: 'Date', render: (v) => fmtDate(v) },
            // [WF-B12] TimeEntrySerializer exposes `created_by_username`
            // and `case_title` â `staff_name`/`staff_details`/
            // `case_reference`/`case_details` never existed, so these
            // columns were blank for every row.
            { key: 'created_by_username', label: 'Staff', render: (v, row) => row.created_by_username || 'â' },
            { key: 'case_title', label: 'Case', render: (v, row) => row.case_title || row.client_full_name || 'â' },
            { key: 'activity_type', label: 'Activity' },
            { key: 'description', label: 'Description' },
            { key: 'duration_hours', label: 'Hours', render: (v, row) => fmtHrs(entryHours(row)), className: 'text-right' },
            { key: 'effective_rate', label: 'Rate (P)', render: (v, row) => fmt(row.effective_rate ?? row.billing_rate), className: 'text-right' },
            { key: 'total_value', label: 'Value (P)', render: (v, row) => fmt(entryValue(row)), className: 'text-right' },
            { key: 'is_billable', label: 'Billable', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {v ? 'Yes' : 'No'}
              </span>
            )},
            { key: 'billing_status', label: 'Billed', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v === 'BILLED' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-600'}`}>
                {v === 'BILLED' ? 'Yes' : 'No'}
              </span>
            )},
          ]}
          exportCsv={{ filename: 'time_entries.csv' }}
          emptyMessage="No time entries found."
        />
      )}

      {subTab === 'expenses' && (
        <AuditorTable
          title={`Expense Entries (${expenses.length})`}
          data={expenses}
          columns={[
            { key: 'date', label: 'Date', render: (v) => fmtDate(v) },
            // [WF-B12] Same fix as the Time Entries table above â
            // ExpenseEntrySerializer exposes `created_by_username`/
            // `case_title`, not `staff_name`/`case_reference`.
            { key: 'created_by_username', label: 'Staff', render: (v, row) => row.created_by_username || 'â' },
            { key: 'case_title', label: 'Case', render: (v, row) => row.case_title || row.client_full_name || 'â' },
            { key: 'category', label: 'Type' },
            { key: 'description', label: 'Description' },
            { key: 'amount', label: 'Amount (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'billable', label: 'Billable', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {v ? 'Yes' : 'No'}
              </span>
            )},
            { key: 'billing_status', label: 'Billed', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v === 'BILLED' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-600'}`}>
                {v === 'BILLED' ? 'Yes' : 'No'}
              </span>
            )},
          ]}
          exportCsv={{ filename: 'expense_entries.csv' }}
          emptyMessage="No expense entries found."
        />
      )}
    </div>
  );
}
